import React from "react";
import { ArrowRight, Scissors, Sparkles, Scale, Wrench } from "lucide-react";
import { formatPrice } from "../../lib/matrix";
import { e5Math, EXTRA_ADDONS } from "../../lib/planner";
import FeatureDiff from "./FeatureDiff";

const money = (v, symbol, est) => (v === null || v === undefined ? "—" : formatPrice(v, symbol, est));
const signed = (v, symbol, est) => (v === null || v === undefined ? "—" : `${v > 0 ? "+" : v < 0 ? "−" : ""}${formatPrice(Math.abs(v), symbol, est)}`);

export default function PlannerMove({ line, plans, rows, priceMode, symbol, onChange, readOnly, addons = [] }) {
  const targets = plans.filter((p) => p.enabled && !p.addon && p.price && (p.group !== "edu" || line.fromPlan?.group === "edu"));
  const targetBaseId = line.to_plan || line.from_plan;
  const suiteOptions = plans.filter((p) => p.enabled && p.addon && (p.requires || []).includes(targetBaseId));
  const extras = line.to_extras || [];
  const extraPrice = (name) => addons.find((a) => a.name.toLowerCase() === name.toLowerCase());
  const math = e5Math({ line, plans, rows, priceMode });
  const set = (patch) => onChange({ from_sku: line.from_sku, from_plan: line.from_plan, seats: line.seats, to_plan: line.to_plan, to_addons: line.to_addons, to_extras: extras, ...patch });

  return (
    <article className={`move glass ${line.changed ? "changed" : ""} ${line.unlicensed ? "unlicensed" : ""}`} data-testid={`move-${line.from_sku}`}>
      {line.remediation && (
        <div className="move-remediation" data-testid={`move-remediation-${line.from_sku}`}>
          <Wrench size={12} /> {line.remediation.finding ? `Security fix (${line.remediation.label}):` : "Gap fix:"} {line.remediation.users.toLocaleString()} users missing {line.remediation.missing.map((m) => ({ entra_p1: "Entra ID P1", entra_p2: "Entra ID P2", intune_p1: "Intune P1" })[m] || m).join(" + ")} → cheapest option <b>{line.remediation.option}</b>
          {line.remediation.alternatives?.length > 0 && <span className="muted"> (also: {line.remediation.alternatives.map((a) => `${a.label} ${money(a.unit, symbol)}`).join(", ")})</span>}
        </div>
      )}
      <div className="move-row">
        <div className="move-from">
          <span className="move-label">Current</span>
          <b>{line.sku?.name || (line.unlicensed ? "Unlicensed users" : line.from_plan)}</b>
          <span className="muted mono">
            {line.unlicensed ? `${line.seats.toLocaleString()} users · no base license` : `${line.sku?.consumed.toLocaleString()} assigned · ${money(line.currentUnit, symbol, line.fromPlan?.estimated)} / user`}
          </span>
        </div>
        <ArrowRight size={18} className="move-arrow" />
        <div className="move-to">
          <span className="move-label">Target</span>
          <div className="move-controls">
            <select value={line.to_plan || ""} onChange={(e) => set({ to_plan: e.target.value || null, to_addons: [] })} disabled={readOnly} className="ws-native-select" data-testid={`move-target-${line.from_sku}`}>
              <option value="">{line.unlicensed ? "No license" : `Keep ${line.fromPlan?.short || ""}`}</option>
              {targets
                .filter((p) => p.id !== line.from_plan)
                .map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
            </select>
            <label className="move-seats">
              <span>seats</span>
              <input type="number" min={0} max={line.sku?.consumed || 999999} value={line.seats} onChange={(e) => set({ seats: parseInt(e.target.value || "0", 10) || 0 })} disabled={readOnly} className="cost-input" data-testid={`move-seats-${line.from_sku}`} />
            </label>
          </div>
          {!line.unlicensed && (
            <div className="move-suites extras">
              {Object.keys(EXTRA_ADDONS).map((name) => {
                const on = extras.includes(name);
                const a = extraPrice(name);
                return (
                  <button
                    key={name}
                    type="button"
                    className="pill"
                    aria-pressed={on}
                    disabled={readOnly}
                    onClick={() => set({ to_extras: on ? extras.filter((x) => x !== name) : [...extras, name] })}
                    data-testid={`move-extra-${line.from_sku}-${name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                  >
                    + {name} <span className="mono">{money(a?.price, symbol, a?.estimated)}</span>
                  </button>
                );
              })}
            </div>
          )}
          {suiteOptions.length > 0 && (
            <div className="move-suites">
              {suiteOptions.map((s) => {
                const on = line.to_addons.includes(s.id);
                return (
                  <button
                    key={s.id}
                    type="button"
                    className="pill"
                    aria-pressed={on}
                    disabled={readOnly}
                    onClick={() => set({ to_addons: on ? line.to_addons.filter((x) => x !== s.id) : [...line.to_addons, s.id] })}
                    data-testid={`move-addon-${line.from_sku}-${s.id}`}
                  >
                    + {s.name.replace("Microsoft ", "").replace(/ \((ENT|FLW|BP)\)/, "")} <span className="mono">{money(s.price?.teams, symbol, s.estimated)}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
        <div className="move-cost">
          <span className="move-label">Per user</span>
          <b className="mono">
            {money(line.currentUnit, symbol)} <ArrowRight size={12} /> {money(line.targetUnit, symbol, line.estimated)}
          </b>
          {line.retireMonthly > 0 && (
            <>
              <span className="move-label">License delta</span>
              <b className={`mono delta ${line.licenseDeltaMonthly > 0 ? "up" : line.licenseDeltaMonthly < 0 ? "down" : ""}`} data-testid={`move-license-delta-${line.from_sku}`}>
                {signed(line.licenseDeltaMonthly, symbol, line.estimated)}
              </b>
              <span className="move-label">Retired add-ons</span>
              <b className="mono delta down" data-testid={`move-retire-${line.from_sku}`}>
                −{formatPrice(line.retireMonthly, symbol)}
              </b>
            </>
          )}
          <span className="move-label">{line.retireMonthly > 0 ? "Net monthly delta" : "Monthly delta"}</span>
          <b className={`mono delta ${line.deltaMonthly > 0 ? "up" : line.deltaMonthly < 0 ? "down" : ""}`} data-testid={`move-delta-${line.from_sku}`}>
            {signed(line.deltaMonthly, symbol, line.estimated)}
          </b>
        </div>
      </div>

      {line.changed && (
        <div className="move-details">
          <FeatureDiff gains={line.gains} losses={line.losses} testid={`diff-${line.from_sku}`} />
          {line.retire.length > 0 && (
            <div className="retire" data-testid={`retire-${line.from_sku}`}>
              <h4>
                <Scissors size={14} /> Standalone add-ons you can retire
              </h4>
              <ul>
                {line.retire.map((r) => (
                  <li key={r.sku.sku_id}>
                    <b>{r.sku.name}</b> <span className="muted">— {r.seats.toLocaleString()} seats now covered by the target ({r.feature_row})</span>
                    <span className="mono ok">{r.monthly === null ? "price n/a" : `−${formatPrice(r.monthly, symbol, r.estimated)} / mo`}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {math && (
            <div className="e5math glass" data-testid={`e5math-${line.from_sku}`}>
              <h4>
                <Scale size={14} /> E5 add-on math
              </h4>
              <div className="e5-cols">
                <div className={`e5-col ${math.cheaper === "bundle" ? "win" : ""}`}>
                  <span className="move-label">Your target</span>
                  <b>{math.bundle.label}</b>
                  <span className="mono big">{money(math.bundle.unit, symbol)} / user</span>
                  <span className="muted mono">{money(math.bundle.monthly, symbol)} / mo for {line.seats.toLocaleString()} seats</span>
                  {math.bundleOnly.length > 0 && <span className="mini-chip ok">+{math.bundleOnly.length} features only here</span>}
                </div>
                <div className={`e5-col ${math.cheaper === "alt" ? "win" : ""}`}>
                  <span className="move-label">Alternative</span>
                  <b>{math.alt.label}</b>
                  <span className="mono big">{money(math.alt.unit, symbol)} / user</span>
                  <span className="muted mono">{money(math.alt.monthly, symbol)} / mo for {line.seats.toLocaleString()} seats</span>
                  {math.altOnly.length > 0 && <span className="mini-chip ok">+{math.altOnly.length} features only here</span>}
                </div>
              </div>
              <p className="e5-verdict">
                <Sparkles size={13} />
                {math.cheaper === "alt" && (
                  <>
                    <b>{math.alt.label}</b> is {formatPrice(math.bundle.unit - math.alt.unit, symbol)} / user cheaper than your target
                    {math.altOnly.length > 0 && <> and adds {math.altOnly.length} features your target lacks (e.g. {math.altOnly.slice(0, 3).map((r) => r.name).join(", ")})</>}
                    {math.bundleOnly.length > 0 && <>; you would give up {math.bundleOnly.length} features only in {math.bundle.label} (e.g. {math.bundleOnly.slice(0, 3).map((r) => r.name).join(", ")})</>}.
                  </>
                )}
                {math.cheaper === "bundle" && (
                  <>
                    <b>{math.bundle.label}</b> is {formatPrice(math.alt.unit - math.bundle.unit, symbol)} / user cheaper than {math.alt.label}
                    {math.bundleOnly.length > 0 && <> and includes {math.bundleOnly.length} features the alternative lacks (e.g. {math.bundleOnly.slice(0, 3).map((r) => r.name).join(", ")})</>}
                    {math.altOnly.length > 0 && <>; the alternative would add {math.altOnly.length} features (e.g. {math.altOnly.slice(0, 3).map((r) => r.name).join(", ")})</>}.
                  </>
                )}
                {math.cheaper === "same" && <>Both options cost the same per user - pick on features.</>}
                {math.cheaper === null && <>Prices missing for this region.</>}
              </p>
            </div>
          )}
        </div>
      )}
    </article>
  );
}
